export class SignalingChannelBase {
    constructor(eventListener, connectionFactory, groupName, isInitiator, previewScope, supportsDataChannels, role, instanceId) {
        this.eventListener = eventListener;
        this.connectionFactory = connectionFactory;
        this.groupName = groupName;
        this.isInitiator = isInitiator;
        this.previewScope = previewScope;
        this.supportsDataChannels = supportsDataChannels;
        this.role = role;
        this.instanceId = instanceId;
        this.joinGroupPromise = null;
        this.peerConnections = {};
        /**
         * The unique client ID assigned by the signaling server upon login.
         */
        this.clientId = null;
        this.isConnected = false;
        this.isDisposed = false;
        // Note: do not register to onbeforeunload events, because the UI allows users to cancel unloading there
        window.addEventListener('unload', () => {
            this.dispose();
        });
    }
    setClientId(clientId) {
        this.clientId = clientId;
        this.role = `${this.role} (${clientId})`;
    }
    //#region virtual/abstract
    createFileTransferFallback() {
        throw new Error('File transfer callback is not implemented.');
    }
    //#endregion virtual/abstract
    getPeers() {
        return this.peerConnections;
    }
    createPeerConnection(peerId, peerScope, peerCanUseWebRTC) {
        if (!this.isConnected) {
            throw `Signaling: unable to create peer connection. The signaling channel is not connected.`;
        }
        // Make sure that the current connection with the peer, if any, is disposed.
        this.disposePeerConnection(peerId);
        const peerConnection = this.connectionFactory.create(this, this.clientId, peerId, peerScope, peerCanUseWebRTC);
        console.log(`${this.role}: created ${this.type} peer connection with ${peerConnection.peerId}.`);
        this.peerConnections[peerId] = peerConnection;
        return peerConnection;
    }
    disposePeerConnection(clientId) {
        const peer = this.peerConnections[clientId];
        if (!peer) {
            return;
        }
        console.log(`${this.role}: disposing connection with '${clientId}'.`);
        peer.dispose();
        delete this.peerConnections[clientId];
    }
    connectToPeer(peerClientId, peerScope, peerInstanceId, peerCanUseWebRTC) {
        console.log(`${this.role}: creating peer ${this.type} connection with ${peerClientId} (scope ${peerScope}, instanceId ${peerInstanceId}) (initiating).`);
        if (peerInstanceId != null && this.instanceId != null && peerInstanceId !== this.instanceId) {
            return console.log(`Not connecting to peer because instanceId ${peerInstanceId} does not match own instanceId ${this.instanceId}.`);
        }
        const peerConnection = this.createPeerConnection(peerClientId, peerScope, peerCanUseWebRTC);
        // Send a message to the peer, asking to also set up a connection with us.
        // The peer will handle this in handlePeerConnectionRequestMessage (below)
        const request = {
            type: 10 /* SignalingMessageType.PeerConnectionRequest */,
            fromClientId: this.clientId,
            toClientId: peerClientId,
            // Pass our own previewScope, instanceId and whether WE can use WebRTC
            scope: this.previewScope,
            instanceId: this.instanceId || undefined,
            canUseWebRTC: true
        };
        this.sendSignalingMessage(request);
        // TODO: we could also wait until the client has ensured a connection with us in handlePeerConnectionRequestMessage, but is this needed?
        this.eventListener.onPeerConnectionCreated(peerConnection, true);
    }
    sendPeerMessage(message) {
        this.sendSignalingMessage(message);
    }
    /**
     * Logs into the signaling server and creates a peer connection when done.
     * @param groupName The name of the group to join.
     */
    joinGroup() {
        // TODO: should we make some timeout mechanism?
        const promise = new Promise((resolve, reject) => {
            this.joinGroupPromise = { resolve, reject };
        });
        const request = {
            type: 2 /* SignalingMessageType.JoinGroupRequest */,
            groupName: this.groupName,
            isHost: this.isInitiator,
            scope: this.previewScope,
            instanceId: this.instanceId || undefined,
            canUseWebRTC: this.supportsDataChannels
        };
        this.sendSignalingMessage(request);
        return promise;
    }
    dispose() {
        if (this.isDisposed) {
            return;
        }
        console.log(`${this.role}: disposing...`);
        this.leaveGroup(); // disposes all peer connections
        this.isDisposed = true;
    }
    /**
     * Disconnects from the current session.
     */
    leaveGroup() {
        if (!this.clientId) {
            return;
        }
        // Say goodbye!
        const msg = { type: 5 /* SignalingMessageType.LeaveGroup */, groupName: this.groupName };
        this.sendSignalingMessage(msg);
        // Dispose all peer connections
        const keys = Object.keys(this.peerConnections);
        for (const clientId of keys) {
            this.disposePeerConnection(clientId);
        }
    }
    /**
     * Disconnects from the current session and connects to a new one.
     */
    changeGroup(groupName) {
        this.leaveGroup();
        this.groupName = groupName;
        return this.joinGroup();
    }
    //#region signaling message handlers
    handleSignalingMessage(message) {
        if (this.isDisposed) {
            console.warn(`${this.role}: received a signaling message while this channel is disposed:`, message);
            return;
        }
        if (!message.type) {
            return console.warn(`${this.role}: received a signaling message that has no message type:`, message);
        }
        switch (message.type) {
            case 3 /* SignalingMessageType.JoinGroupResponse */:
                this.handleJoinGroupResponseMessage(message);
                break;
            case 4 /* SignalingMessageType.JoinedGroup */:
                this.handleJoinedGroupMessage(message);
                break;
            case 10 /* SignalingMessageType.PeerConnectionRequest */:
                this.handlePeerConnectionRequestMessage(message);
                break;
            case 6 /* SignalingMessageType.LeftGroup */:
                this.handleLeftGroupMessage(message);
                break;
            case 1 /* SignalingMessageType.Log */:
                this.handleLogMessage(message);
                break;
            case 8 /* SignalingMessageType.RTCOffer */:
                this.handleOfferMessage(message);
                break;
            case 7 /* SignalingMessageType.RTCIceCandidate */:
                this.handleIceCandidateMessage(message);
                break;
            case 9 /* SignalingMessageType.RTCAnswer */:
                this.handleAnswerMessage(message);
                break;
            case 11 /* SignalingMessageType.PreviewFallback */:
                this.handlePeerMessage(message);
                break;
            default:
                console.warn(`${this.role}: received unsupported signaling message with type '${message.type}'.`);
                break;
        }
    }
    handleJoinedGroupMessage(message) {
        console.log(`${this.role}: peer ${message.clientId} has joined.`);
        this.eventListener.onPeerJoined(this, message);
    }
    /**
     * Called when a user logged into (wether successful or not) the signaling server.
     */
    handleJoinGroupResponseMessage(response) {
        // This is a response to join(). Resolve it.
        if (!response.success) {
            console.warn(`${this.role}: joining group '${response.groupName}' has failed':`, response.reason); // don't reject the promise, this is no error
        }
        // Remember our own client ID
        this.setClientId(response.clientId);
        if (this.joinGroupPromise) {
            // resolve the call to joinGroup()
            this.joinGroupPromise.resolve(response);
            this.joinGroupPromise = null;
        }
    }
    handleLeftGroupMessage(message) {
        console.log(`${this.role}: peer '${message.clientId}' has left.`);
        this.disposePeerConnection(message.clientId);
    }
    /**
     * Handles log messages received from the signaling server.
     */
    handleLogMessage(message) {
        switch (message.level) {
            case 1 /* LogLevel.Info */:
                console.error(`Signaling server:`, message.message);
                break;
            case 2 /* LogLevel.Warning */:
                console.warn(`Signaling server:`, message.message);
                break;
            case 3 /* LogLevel.Error */:
                console.error(`Signaling server:`, message.message);
                break;
            default:
                console.log(`Signaling server:`, message.message);
                break;
        }
    }
    handlePeerConnectionRequestMessage(message) {
        if (message.instanceId != null && this.instanceId != null && message.instanceId !== this.instanceId) {
            return console.log(`${this.role}: ignoring peer connection request because instanceId '${message.instanceId}' does not match own instanceId ${this.instanceId}.`);
        }
        console.log(`${this.role}: creating peer connection with ${message.fromClientId} (requested by peer with scope: ${message.scope}).`);
        const peerConnection = this.createPeerConnection(message.fromClientId, message.scope, message.canUseWebRTC);
        this.eventListener.onPeerConnectionCreated(peerConnection, false);
    }
    /**
    * Received when a peer sends an ICE candidate to us.
    */
    handleIceCandidateMessage(message) {
        const peer = this.peerConnections[message.fromClientId];
        if (!peer) {
            throw `${this.role}: unable to handle ICE candidate. There is no peer with client ID '${message.fromClientId}'.`;
        }
        peer.handleIceCandidateMessage(message);
    }
    /**
     * Called when a the initiator sends us an offer.
     */
    handleOfferMessage(offer) {
        const peer = this.peerConnections[offer.fromClientId];
        if (!peer)
            throw `${this.role}: unable to handle offer. There is no peer with client ID '${offer.fromClientId}'. Own client ID: ${this.clientId}.`;
        peer.handleOfferMessage(offer);
    }
    handleAnswerMessage(answer) {
        const peer = this.peerConnections[answer.fromClientId];
        if (!peer)
            throw `${this.role}: unable to handle answer. There is no peer with client ID '${answer.fromClientId}'.`;
        peer.handleAnswerMessage(answer);
    }
    handlePeerMessage(message) {
        const peer = this.peerConnections[message.fromClientId];
        if (!peer)
            throw `${this.role}: unable to handle peer message. There is no peer with client ID '${message.fromClientId}'.`;
        peer.handlePeerMessage(message);
    }
}
//# sourceMappingURL=signaling-channel-base.js.map